import { fetchFrequentlyBuyTogetherProduct } from 'components/FrequentlyBuyTogether/actions';
import { push } from 'connected-react-router';
import { resetOrderDraftToken, setCheckout, setCheckoutTotal } from 'containers/CheckoutPage/actions';
import { selectItemDiscount } from 'containers/CheckoutPage/selectors';
import { updateItemCart } from 'containers/MainLayout/actions';
import MainActionTypes from 'containers/MainLayout/constants';
import { getStore } from 'containers/MainLayout/saga';
import { selectCart } from 'containers/MainLayout/selectors';
import { flatMap } from 'lodash';
import { all, call, fork, put, select, take } from 'redux-saga/effects';
import { CartItem, ItemDiscount } from 'types/schema';
import { getBuyer, removeCartItemLight, removeCartSupplierLight } from 'utils/apollo';
import calculateVAT from 'utils/calculateVAT';
import { KAMEREO_SUPPLIER_ID } from 'utils/constants';
import ActionTypes from './constants';
import { showToast } from 'components/ToastNotification';
import translations from 'translations';
import messages from 'containers/CheckoutPage/messages';
import utilsMessages from 'utils/messages';

export function computeCheckoutQuantity(cartSet: any) {
  return flatMap(cartSet, (singleSupplierCart) => singleSupplierCart.items).reduce((agg, item) => {
    agg[item.product.id] = Number(Number(item.quantity).toFixed(3));
    return agg;
  }, {});
}

export function computeCheckoutSupplierTotals(cartSet: any) {
  return cartSet.reduce((agg, singleSupplierCart) => {
    agg[singleSupplierCart.supplier.id] = singleSupplierCart.total;
    return agg;
  }, {});
}

export function computeCheckoutSupplierTotalBeforeVAT(cartSet: any) {
  return cartSet.reduce((agg, singleSupplierCart) => {
    const totalBeforeVAT = singleSupplierCart.items.reduce((acc, item) => {
      const totalBeforeVATofItem =
        calculateVAT(item?.product?.priceV2?.price, item?.product?.priceV2?.vat, item?.product?.priceV2?.vatIncluded)
          .beforeVat * item?.quantity;
      return acc + totalBeforeVATofItem;
    }, 0);
    agg[singleSupplierCart.supplier.id] = totalBeforeVAT;
    return agg;
  }, {});
}

export function computeCheckoutCartTotal(cartSet: any) {
  return cartSet.reduce((total, singleSupplierCart) => total + singleSupplierCart.total, 0);
}

export function computeCheckoutCartTotalAfterItemDiscount(cartSet: any, itemDiscount: ItemDiscount[]) {
  return cartSet.reduce((total, singleSupplierCart) => {
    const totalBeforeDiscountVoucher = singleSupplierCart?.total || 0;

    const totalDiscount = singleSupplierCart.items.reduce((acc, item) => {
      const discountOfItemBeforeVat = itemDiscount?.find((it) => it?.id === item?.product?.id)?.discountValue || 0;
      const discountOfItemAfterVat = Math.round((discountOfItemBeforeVat * (item?.product?.priceV2?.vat + 100)) / 100);

      return acc + discountOfItemAfterVat;
    }, 0);
    return total + totalBeforeDiscountVoucher - totalDiscount;
  }, 0);
}

export function computeCheckoutQuantityAndTotal(cartSet: any, itemDiscount: ItemDiscount[]) {
  return {
    quantity: computeCheckoutQuantity(cartSet),
    totalOfSupplier: computeCheckoutSupplierTotals(cartSet),
    totalBeforeVATOfSupplier: computeCheckoutSupplierTotalBeforeVAT(cartSet),
    total: computeCheckoutCartTotal(cartSet),
    totalAfterItemDiscount: computeCheckoutCartTotalAfterItemDiscount(cartSet, itemDiscount),
  };
}

export function* checkStore(store) {
  const buyer = yield call(getBuyer);
  if (buyer?.id) {
    const isStoreAvailable = buyer?.stores?.some((storeEl) => storeEl?.id === store?.id);
    if (!isStoreAvailable) {
      yield put(push('/change-store'));
    }
  }
}

function* initCartFlow() {
  let store = yield call(getStore);
  const loggedIn = !!localStorage.getItem('token');
  if (!store && loggedIn) {
    yield take(MainActionTypes.SET_SELECTED_STORE);
    store = yield call(getStore);
  }

  const cart = yield select(selectCart());
  const itemDiscount = yield select(selectItemDiscount());
  const checkoutQuantityAndTotalResult = computeCheckoutQuantityAndTotal(cart.cartSet, itemDiscount);
  yield put(setCheckout(checkoutQuantityAndTotalResult));
  yield put(setCheckoutTotal(checkoutQuantityAndTotalResult.total));

  yield put(fetchFrequentlyBuyTogetherProduct({}));

  // NOTE: new flow do not automatically remove not available product, instead, it will show the the product and let user decide to remove or not
  // if (cart.notAvailableProducts.length > 0) {
  //   yield all(
  //     cart.notAvailableProducts.map((item: CartItem) => {
  //       return call(removeCartItemLight, {
  //         input: { storeId: store.id, supplierId: KAMEREO_SUPPLIER_ID, productId: item?.product?.id },
  //       });
  //     }),
  //   );
  // }

  // check deactivated store
  if (loggedIn) {
    yield call(checkStore, store);
  }
}

function* initCheckoutStep1Flow() {
  yield call(initCartFlow);
}

function* removeAllNotAvailableProductsFlow(action) {
  const {
    payload: { items },
  } = action;

  const store = yield call(getStore);

  if (!store || !items?.length) return;

  for (const item of items as CartItem[]) {
    yield call(removeCartItemLight, {
      input: { storeId: store.id, supplierId: KAMEREO_SUPPLIER_ID, productId: item?.product?.id },
    });
  }

  yield put(updateItemCart.success());
}

function* removeAllProductFlow(action) {
  const { payload } = action;
  const store = yield call(getStore);

  const response = yield call(removeCartSupplierLight, {
    storeId: store?.id,
    ...payload,
  });

  if (!response.errors) {
    yield put(updateItemCart.success());
    showToast({ message: translations(messages.removeAllProductsSuccess) });
  } else {
    showToast({ message: translations(utilsMessages.deleteFailed), type: 'error' });
  }
}

export default function* checkoutStep1PageSaga() {
  yield call(initCartFlow);
  yield all(
    [
      [ActionTypes.REMOVE_ALL_PRODUCT, removeAllProductFlow],
      [ActionTypes.REMOVE_ALL_NOT_AVAILABLE_PRODUCTS, removeAllNotAvailableProductsFlow],
      [MainActionTypes.FETCH_CART_SUCCESS, initCartFlow],
    ].map(([actionName, handler]) =>
      fork(function* () {
        while (true) {
          const action = yield take(actionName as any);
          yield call(handler as any, action as any);
        }
      }),
    ),
  );
  yield put(resetOrderDraftToken());
}
